import type { CompanySelectDto } from '@@types/api.types';
import { CompanyProfileModal } from '@components/pages/company/components/company-company-profile-modal.component';
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu';
import { useSidebar } from '@components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip';
import { useIsMobile } from '@hooks/mobile.hook';
import { cn } from '@lib/utils';
import { useGetUserSelectedCompanyId, useUpdateSelectedCompany } from '@queries/users.query';
import { Building2, Check, ChevronsUpDown, Plus } from 'lucide-react';
import React, { useState } from 'react';

type OrganizationSwitcherProps = {
  companies: CompanySelectDto[];
};

export const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = (props) => {
  const { companies } = props;
  const { state } = useSidebar();
  const isMobile = useIsMobile();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const { mutate: updateSelectedCompany } = useUpdateSelectedCompany();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId) || companies[0];

  if (!companies || companies.length === 0) {
    return null;
  }

  const handleCompanyCreated = (newCompanyId: string) => {
    updateSelectedCompany({ companyId: newCompanyId });
  };

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
  };

  const handleContinue = () => {
    setIsCreateModalOpen(false);
  };

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        className={cn(
          'flex items-center border border-white/20! cursor-pointer gap-2 rounded-md transition-colors',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
          state === 'expanded' ? 'px-2 py-2 w-full' : 'p-0 w-auto mx-2',
          'ßgroup-data-[collapsible=icon]:justify-center',
        )}
      >
        <Avatar className="h-8 w-8 rounded-md shrink-0">
          {selectedCompany.logoUrl && <AvatarImage src={selectedCompany.logoUrl} />}
          <AvatarFallback className="rounded-md bg-transparent">
            <Building2 className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col items-start flex-1 min-w-0 group-data-[collapsible=icon]:hidden group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:w-0 transition-[opacity,width] group-data-[collapsible=icon]:duration-75">
          <span className="text-sm font-medium truncate w-full text-left">{selectedCompany.name}</span>
        </div>
        <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 group-data-[collapsible=icon]:hidden group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:w-0 transition-[opacity,width] group-data-[collapsible=icon]:duration-75" />
      </button>
    </DropdownMenuTrigger>
  );

  return (
    <>
      <DropdownMenu>
        {state === 'collapsed' && !isMobile ? (
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent
              align="center"
              side="right"
              className="bg-sidebar border-sidebar-border text-sidebar-foreground"
            >
              {selectedCompany.name}
            </TooltipContent>
          </Tooltip>
        ) : (
          trigger
        )}
        <DropdownMenuContent
          align="start"
          className="w-64"
        >
          {companies.map((company) => {
            const isSelected = company.id === selectedCompany.id;
            return (
              <DropdownMenuItem
                key={company.id}
                onClick={async (e) => {
                  e.stopPropagation();
                  updateSelectedCompany({ companyId: company.id });
                }}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Avatar className="h-6 w-6 rounded-md">
                  {company.logoUrl && <AvatarImage src={company.logoUrl} />}
                  <AvatarFallback className="rounded-md">
                    <Building2 className="h-3 w-3" />
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 truncate">{company.name}</span>
                {isSelected && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setIsCreateModalOpen(true);
            }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Avatar className="h-6 w-6 rounded-md">
              <AvatarFallback className="rounded-md">
                <Plus className="h-3 w-3" />
              </AvatarFallback>
            </Avatar>
            <span className="flex-1">Create Organization</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {isCreateModalOpen && (
        <CompanyProfileModal
          isOpen={isCreateModalOpen}
          onClose={handleCreateModalClose}
          onCompanyCreated={handleCompanyCreated}
          onContinue={handleContinue}
        />
      )}
    </>
  );
};
